/**
 * Client-side prediction.
 *
 * Runs `@chaos/sim`'s `stepWorld` locally, immediately, on the LOCAL PLAYER
 * ONLY, so pressing forward moves you this frame instead of one round-trip
 * later. Same code as the server; the only difference is who is allowed to be
 * right.
 *
 * ── What is predicted, and what is not ────────────────────────────────────
 *
 * PREDICTED: position, velocity, facing, grounded, jump/coyote/buffer state, the
 * dash, and the self-movement half of an ability (blink, perch). These are pure
 * functions of your own input and the map, so the server will agree.
 *
 * NOT PREDICTED: health, damage, death, kills, ult charge, and other players.
 * Those depend on eight players' inputs and the server's ordering, so guessing
 * them produces a wrong number on screen that has to be visibly corrected — and
 * in a wagered match "your health bar lied to you" is not an acceptable UX bug.
 *
 * The local world therefore contains exactly one player, and every non-positional
 * field on that player is overwritten from the authoritative snapshot on each
 * reconcile. `restoreAuthoritative` is what keeps a locally-predicted zone tick
 * from killing a player the server considers alive.
 *
 * PREDICTED (since the chaos wire fix): the director's mechanical mutations.
 * Low gravity, ice, conveyors and speed surges are global physics, not another
 * player's hidden input — refusing to predict them meant every step of a
 * two-minute mutator diverged, and the reconciler replayed each correction
 * through the same unmutated physics, so the player stuttered for the whole
 * duration. `syncChaos` folds the snapshot's authoritative ledger in;
 * `applyChaosEvent` applies the landing the moment the event arrives.
 */

import { createWorld, createFlatMap, stepWorld, applyMutation, isMutation } from '@chaos/sim';

/**
 * Per-tick decay of the render-only correction offset, and the magnitude below
 * which it snaps to zero. 0.65^tick puts a 1m correction under 2cm in ~9 ticks
 * (~300ms) — long enough that a Seismic Slam knockback reads as being THROWN
 * rather than teleported, short enough that the drawn body never lags the
 * authoritative one by a reactable amount.
 */
const CORRECTION_DECAY = 0.65;
const CORRECTION_FLOOR = 0.02;

export class Predictor {
  /**
   * @param {object} opts
   * @param {string} opts.playerId   server-side user id (NOT the sessionId)
   * @param {string} opts.characterId
   * @param {object} opts.map        MapDef from @chaos/sim
   * @param {string} opts.seed       any stable string; the local world's RNG is
   *                                 never consulted for anything that matters
   */
  constructor({ playerId, characterId, map, seed = 'client-prediction' }) {
    this.playerId = playerId;
    this.map = map ?? createFlatMap();
    this.world = createWorld({
      seed,
      map: this.map,
      players: [{ id: playerId, characterId }],
    });
    this.player = this.world.players[0];

    // The zone is the server's business. A predicted zone would tick damage on a
    // client whose zone centre came from a different RNG stream than the
    // server's, and predicted damage is exactly what this module refuses to do.
    //
    // `startRadius` is the lever, not `radius` or `dps`: `stepZone` recomputes
    // both of those from the tick on every single step, so setting them here
    // would last exactly one tick. An effectively infinite start radius means
    // nobody is ever outside the circle and the damage loop never fires.
    this.world.zone.startRadius = Number.MAX_SAFE_INTEGER;
    this.world.zone.radius = Number.MAX_SAFE_INTEGER;
    this.world.zone.dps = 0;

    this._inputs = new Map();
    /** Position at the start of the current tick, for render interpolation. */
    this.prev = { x: this.player.x, y: this.player.y, z: this.player.z, yaw: this.player.yaw };
    this.curr = { ...this.prev };

    /**
     * Render-only correction offset, prev/curr pairs like the body so the same
     * `alpha` drives both. When the reconciler snaps a divergence too large for
     * input replay to explain (a knockback, a Gravity Well pull), the drawn
     * body starts from where it WAS on screen and bleeds onto the authoritative
     * position over a few ticks — the simulation, hit tests and every consumer
     * of `this.player` see only the snapped truth. Decayed per tick rather than
     * per wall-clock read: this module steps a deterministic world and keeps
     * clocks out entirely.
     */
    this._smoothPrev = { x: 0, y: 0, z: 0 };
    this._smoothCurr = { x: 0, y: 0, z: 0 };
    /** Last output of `interpolate` — the position actually on screen. */
    this._lastRender = null;

    /** Last authoritative non-positional state, re-applied after every step. */
    this._auth = {
      health: this.player.health,
      phase: 'alive',
      ultCharge: 0,
    };

    // The floor-mutator overlay reads its chaos ledger straight off this world
    // — the predictor is the only structured view of "which mutations are live"
    // a production client has. Handed over through a global rather than an
    // import: ArenaRenderer already discovers its world source by probing
    // globals (the gym/debug handles), and importing it here would pull the
    // whole three/webgpu stack into a module that otherwise runs headless.
    // Registered per construction so each match's fresh predictor replaces the
    // previous world without anyone remembering to rewire it.
    globalThis.__chaosPredictedWorld = () => this.world;
  }

  /**
   * Applies a mutation the moment its `chaos_event` lands, instead of waiting
   * up to a snapshot for `syncChaos` to catch it. Announce-phase events carry
   * the payload too (for HUD preview) but must not touch physics — the director
   * can still suppress a warned event before it lands.
   *
   * Ambient kinds pass through `applyMutation`'s own no-op; a malformed payload
   * is dropped rather than thrown, because a bad message from the server is not
   * worth crashing the render loop over — the snapshot ledger self-heals it.
   */
  applyChaosEvent(ev) {
    if (ev?.phase !== 'apply' || !isMutation(ev.mutation)) return;
    applyMutation(this.world, ev.mutation);
  }

  /**
   * Converges the local chaos ledger onto the snapshot's authoritative one.
   *
   * Entries are matched by kind and overlapping window: an `applyChaosEvent`
   * landing computed its window from the local tick, which can sit a frame off
   * the server's, so an overlapping same-kind entry is ADOPTED into the
   * server's exact window rather than duplicated. Expired local entries are
   * left alone — `isActive` windows make them inert, and the reconciler replays
   * recent ticks whose physics they may still govern.
   */
  syncChaos(entries) {
    if (!entries) return;
    const applied = this.world.chaos.applied;
    for (const e of entries) {
      if (!e || !isMutation(e.mutation)) continue;
      let found = false;
      for (const a of applied) {
        if (a.kind !== e.kind) continue;
        if (a.tick < e.untilTick && e.tick < a.untilTick) {
          a.tick = e.tick;
          a.untilTick = e.untilTick;
          a.mutation = e.mutation;
          found = true;
          break;
        }
      }
      if (!found) {
        applied.push({ kind: e.kind, tick: e.tick, untilTick: e.untilTick, mutation: e.mutation });
      }
    }
  }

  /**
   * Advances the local world by one tick with one input frame.
   *
   * Returns the events the local sim produced. They are used ONLY for immediate
   * self-feedback (a cast wind-up, a dash whoosh) — never for damage, never for
   * a kill, never for anything another player can see.
   */
  step(frame) {
    this.prev.x = this.player.x;
    this.prev.y = this.player.y;
    this.prev.z = this.player.z;
    this.prev.yaw = this.player.yaw;

    // The correction offset ages one tick per step, mirroring prev/curr, so
    // interpolate() bleeds it off continuously instead of in 30Hz stair-steps.
    // Replay steps decay it too, harmlessly: a reconcile that needs smoothing
    // overwrites both halves right after its replay.
    this._smoothPrev.x = this._smoothCurr.x;
    this._smoothPrev.y = this._smoothCurr.y;
    this._smoothPrev.z = this._smoothCurr.z;
    this._smoothCurr.x *= CORRECTION_DECAY;
    this._smoothCurr.y *= CORRECTION_DECAY;
    this._smoothCurr.z *= CORRECTION_DECAY;
    const s = this._smoothCurr;
    if (Math.abs(s.x) + Math.abs(s.y) + Math.abs(s.z) < CORRECTION_FLOOR) {
      s.x = s.y = s.z = 0;
    }

    this._inputs.clear();
    this._inputs.set(this.playerId, frame);

    // `stepWorld` refuses to advance a finished world. A client whose clock
    // drifted past `matchEndTick` would silently stop moving, so the flag is
    // cleared here — the server, not this world, decides when the match is over.
    this.world.ended = false;
    const events = stepWorld(this.world, this._inputs);

    this.restoreAuthoritative();

    this.curr.x = this.player.x;
    this.curr.y = this.player.y;
    this.curr.z = this.player.z;
    this.curr.yaw = this.player.yaw;
    return events;
  }

  /**
   * Re-asserts server truth over anything the local step invented.
   *
   * Without this a client that walks into the (locally wrong) zone predicts
   * itself to death, `phase` flips to "dead", and movement prediction stops for
   * a player the server still considers alive.
   */
  restoreAuthoritative() {
    this.player.health = this._auth.health;
    this.player.phase = this._auth.phase;
    this.player.ultCharge = this._auth.ultCharge;
    this.world.ended = false;
    this.world.winnerId = null;
    this.world.endReason = null;
    // Lethal geometry (the void, lava) can still push a death record before we
    // get here. Clearing it keeps the local world from accumulating deaths that
    // never happened, which would otherwise grow without bound over four minutes.
    if (this.world.deaths.length > 0) this.world.deaths.length = 0;
  }

  /**
   * Places the body at its committed spawn.
   *
   * The spawn comes from `world_init` — it is part of the seed commitment, so
   * this is the server's number, not a local guess. Without it the local world's
   * own seeded shuffle would put the player somewhere else for the whole drop.
   */
  setSpawn(x, y, z, yaw = 0) {
    this.player.x = x;
    this.player.y = y;
    this.player.z = z;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.vz = 0;
    this.player.yaw = yaw;
    this.prev = { x, y, z, yaw };
    this.curr = { x, y, z, yaw };
    // A spawn is a real teleport. Smoothing across it would draw the body
    // sliding from wherever the lobby camera left it.
    this.clearCorrectionSmoothing();
    this._lastRender = null;
  }

  /**
   * Where the body was last DRAWN — `interpolate`'s most recent output, offset
   * included. The reconciler captures this before a snap so the smoothing
   * offset starts from the exact on-screen position, not from a tick-boundary
   * approximation of it.
   */
  captureVisual(out) {
    const src = this._lastRender;
    if (src) {
      out.x = src.x;
      out.y = src.y;
      out.z = src.z;
    } else {
      out.x = this.curr.x + this._smoothCurr.x;
      out.y = this.curr.y + this._smoothCurr.y;
      out.z = this.curr.z + this._smoothCurr.z;
    }
    return out;
  }

  /**
   * Starts bleeding a correction off. Both halves get the full offset so the
   * first frame after the snap renders exactly where the previous frame did —
   * zero visible jump — and the decay begins on the next step.
   */
  beginCorrectionSmoothing(dx, dy, dz) {
    this._smoothPrev.x = this._smoothCurr.x = dx;
    this._smoothPrev.y = this._smoothCurr.y = dy;
    this._smoothPrev.z = this._smoothCurr.z = dz;
  }

  clearCorrectionSmoothing() {
    this._smoothPrev.x = this._smoothPrev.y = this._smoothPrev.z = 0;
    this._smoothCurr.x = this._smoothCurr.y = this._smoothCurr.z = 0;
  }

  /**
   * Folds an authoritative player snapshot into the local world.
   *
   * `snapPosition` false means only the non-positional fields are taken — the
   * normal case, where prediction was close enough that snapping would be a
   * visible twitch for no benefit.
   */
  applySnapshot(snap, { snapPosition }) {
    this._auth.health = snap.health;
    this._auth.phase = snap.phase;
    this._auth.ultCharge = snap.ultCharge;

    this.player.health = snap.health;
    this.player.phase = snap.phase;
    this.player.ultCharge = snap.ultCharge;
    // Cooldowns are absolute server ticks, so they are only meaningful next to a
    // server-aligned `world.tick`. Both are taken together, always.
    this.player.cooldowns = { ...snap.cooldowns };

    if (snapPosition) {
      this.player.x = snap.x;
      this.player.y = snap.y;
      this.player.z = snap.z;
      this.player.vx = snap.vx;
      this.player.vy = snap.vy;
      this.player.vz = snap.vz;
      this.player.yaw = snap.yaw;
    }
  }

  /** Aligns the local clock with the server's. Cooldown maths depends on it. */
  setTick(tick) {
    this.world.tick = tick;
  }

  /** Distance between the predicted body and an authoritative position. */
  errorFrom(snap) {
    const dx = this.player.x - snap.x;
    const dy = this.player.y - snap.y;
    const dz = this.player.z - snap.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * Render position for a partial tick. `alpha` is 0..1 within the tick.
   *
   * The correction offset rides on top of the authoritative lerp — this is the
   * ONLY place it is ever added, which is what makes the smoothing render-only:
   * ability origins, hit feedback and the debug overlay all read `this.player`
   * and see the snapped truth.
   */
  interpolate(alpha, out) {
    out.set(
      this.prev.x + (this.curr.x - this.prev.x) * alpha +
        this._smoothPrev.x + (this._smoothCurr.x - this._smoothPrev.x) * alpha,
      this.prev.y + (this.curr.y - this.prev.y) * alpha +
        this._smoothPrev.y + (this._smoothCurr.y - this._smoothPrev.y) * alpha,
      this.prev.z + (this.curr.z - this.prev.z) * alpha +
        this._smoothPrev.z + (this._smoothCurr.z - this._smoothPrev.z) * alpha
    );
    if (!this._lastRender) this._lastRender = { x: 0, y: 0, z: 0 };
    this._lastRender.x = out.x;
    this._lastRender.y = out.y;
    this._lastRender.z = out.z;
    return out;
  }
}
